import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const CompanySettings = () => {
  const [companyData, setCompanyData] = useState({
    company_name: '',
    company_code: '',
    address: '',
    city: '',
    state: '',
    country: '',
    phone: '',
    email: '',
    gst_number: '',
    pf_registration: '',
    esi_registration: '',
    it_tan: '',
    // New fields from companies table
    pf_percentage: 0,
    pf_employer_percentage: 0,
    pf_ceiling: 0,
    esi_percentage: 0,
    esi_employer_percentage: 0,
    esi_ceiling: 0,
    photo: '',
    status: 'Active'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [previewImage, setPreviewImage] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);

  useAuth();
  const navigate = useNavigate();

  const API_BASE = process.env.REACT_APP_API_BASE;
  const API_BASE_1 = process.env.REACT_APP_API_BASE_1;

  const fetchCompanyData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');

      const response = await axios.get(`${API_BASE}/get_user_company.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.company) {
        const company = response.data.company;
        setCompanyData({
          company_name: company.company_name || '',
          company_code: company.company_code || '',
          address: company.address || '',
          city: company.city || '',
          state: company.state || '',
          country: company.country || '',
          phone: company.phone || '',
          email: company.email || '',
          gst_number: company.gst_number || '',
          pf_registration: company.pf_registration || '',
          esi_registration: company.esi_registration || '',
          it_tan: company.it_tan || '',
          // New fields
          pf_percentage: company.pf_percentage || 0,
          pf_employer_percentage: company.pf_employer_percentage || 0,
          pf_ceiling: company.pf_ceiling || 0,
          esi_percentage: company.esi_percentage || 0,
          esi_employer_percentage: company.esi_employer_percentage || 0,
          esi_ceiling: company.esi_ceiling || 0,
          photo: company.photo || '',
          status: company.status || 'Active'
        });

        if (company.photo && company.photo.trim() !== '') {
          if (company.photo.startsWith('http')) {
            setPreviewImage(company.photo);
          } else {
            setPreviewImage(`${API_BASE_1}/${company.photo}`);
          }
        }
      } else {
        setError(response.data.error || 'Failed to load company data');
      }
    } catch (error) {
      console.error('Error fetching company data:', error);
      setError('Failed to load company data. Please check your connection.');
    } finally {
      setLoading(false);
    }
  }, [API_BASE, API_BASE_1]);

  useEffect(() => {
    fetchCompanyData();
  }, [fetchCompanyData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCompanyData(prev => ({
      ...prev,
      [name]: name.includes('percentage') || name.includes('ceiling') ? parseFloat(value) || 0 : value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check if file is an image
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }

      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size should be less than 5MB');
        return;
      }

      setPhotoFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setPhotoFile(null);
    setPreviewImage(null);
    setCompanyData(prev => ({
      ...prev,
      photo: ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const token = localStorage.getItem('authToken');
      const formData = new FormData();

      // Add all form fields to FormData
      Object.keys(companyData).forEach(key => {
        if (key !== 'photo') {
          formData.append(key, companyData[key]);
        }
      });

      // Add photo file if selected
      if (photoFile) {
        formData.append('photo', photoFile);
      }

      // If photo is being removed
      if (!photoFile && companyData.photo === '') {
        formData.append('remove_photo', '1');
      }

      const response = await axios.post(
        `${API_BASE}/update_company_settings.php`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        setSuccess('Company settings updated successfully!');

        // Update preview if photo was uploaded
        if (response.data.photo_url) {
          setPreviewImage(response.data.photo_url);
          setCompanyData(prev => ({
            ...prev,
            photo: response.data.photo_path || ''
          }));
        }

        // Clear file selection
        setPhotoFile(null);

        // Scroll to top
        window.scrollTo(0, 0);
      } else {
        setError(response.data.error || 'Failed to update company settings');
      }
    } catch (error) {
      console.error('Error updating company settings:', error);
      setError(error.response?.data?.error || 'Failed to update company settings');
    } finally {
      setSaving(false);
    }
  };

  // Navigation handlers
  const goToDashboard = () => {
    navigate('/dashboard');
  };

  const goToHome = () => {
    navigate('/');
  };

  const goToAdminPanel = () => {
    navigate('/admin-panel');
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center youtube-page-container" style={{ height: '50vh' }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading company settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4 youtube-page-container">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-body py-4">
              <div className="row align-items-center">
                <div className="col-md-6">
                  <div className="d-flex align-items-center">
                    <div className="btn-group me-3" role="group">
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={goToHome}
                        title="Go to Home"
                      >
                        <i className="bi bi-house"></i>
                      </button>
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={goToDashboard}
                        title="Go to Dashboard"
                      >
                        <i className="bi bi-speedometer2"></i>
                      </button>
                    </div>
                    <div>
                      <h2 className="h4 mb-1 fw-bold text-dark">
                        <i className="bi bi-building me-2 text-primary"></i>
                        Company Settings
                      </h2>
                      <p className="text-muted mb-0">
                        Manage your company information and logo
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-md-6 text-end">
                  <div className="d-flex justify-content-end align-items-center">
                    <div className="btn-group me-2" role="group">
                      <button
                        className="btn btn-outline-warning"
                        onClick={goToAdminPanel}
                        title="Go to Admin Panel"
                      >
                        <i className="bi bi-gear me-2"></i>
                        Admin Panel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Navigation */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card bg-light border-0">
            <div className="card-body py-3">
              <div className="row text-center">
                <div className="col-md-4 mb-2 mb-md-0">
                  <button
                    className="btn btn-outline-primary w-100 d-flex align-items-center justify-content-center"
                    onClick={goToHome}
                  >
                    <i className="bi bi-house me-2"></i>
                    Home
                  </button>
                </div>
                <div className="col-md-4 mb-2 mb-md-0">
                  <button
                    className="btn btn-outline-success w-100 d-flex align-items-center justify-content-center"
                    onClick={goToDashboard}
                  >
                    <i className="bi bi-speedometer2 me-2"></i>
                    Dashboard
                  </button>
                </div>
                <div className="col-md-4">
                  <button
                    className="btn btn-outline-warning w-100 d-flex align-items-center justify-content-center"
                    onClick={goToAdminPanel}
                  >
                    <i className="bi bi-gear me-2"></i>
                    Admin Panel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="alert alert-danger d-flex align-items-center" role="alert">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              <div>{error}</div>
              <button
                type="button"
                className="btn-close ms-auto"
                onClick={() => setError('')}
              ></button>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="alert alert-success d-flex align-items-center" role="alert">
              <i className="bi bi-check-circle-fill me-2"></i>
              <div>{success}</div>
              <button
                type="button"
                className="btn-close ms-auto"
                onClick={() => setSuccess('')}
              ></button>
            </div>
          </div>
        </div>
      )}

      {/* Main Form */}
      <div className="row">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  {/* Left Column - Company Logo */}
                  <div className="col-lg-4 mb-4">
                    <div className="card border-0 bg-light">
                      <div className="card-body text-center">
                        <h5 className="card-title mb-4">
                          <i className="bi bi-image me-2"></i>
                          Company Logo
                        </h5>

                        <div className="mb-3">
                          {previewImage ? (
                            <div className="text-center">
                              <img
                                src={previewImage}
                                alt="Company Logo"
                                className="img-fluid rounded border"
                                style={{ maxHeight: '200px' }}
                              />
                            </div>
                          ) : (
                            <div className="border rounded d-flex align-items-center justify-content-center"
                              style={{ height: '200px', backgroundColor: '#f8f9fa' }}>
                              <i className="bi bi-building display-4 text-muted"></i>
                            </div>
                          )}
                        </div>

                        <div className="d-grid gap-2">
                          <div className="input-group">
                            <input
                              type="file"
                              className="form-control"
                              id="companyLogo"
                              accept="image/*"
                              onChange={handleFileChange}
                            />
                            <label className="input-group-text" htmlFor="companyLogo">
                              <i className="bi bi-upload"></i>
                            </label>
                          </div>

                          {previewImage && (
                            <button
                              type="button"
                              className="btn btn-outline-danger"
                              onClick={handleRemoveLogo}
                            >
                              <i className="bi bi-trash me-2"></i>
                              Remove Logo
                            </button>
                          )}
                        </div>

                        <small className="text-muted d-block mt-2">
                          Recommended: Square image, max 5MB
                        </small>
                      </div>
                    </div>
                  </div>

                  {/* Right Column - Company Details */}
                  <div className="col-lg-8">
                    <div className="row g-3">
                      {/* Basic Information */}
                      <div className="col-12">
                        <h5 className="border-bottom pb-2 mb-3">
                          <i className="bi bi-info-circle me-2"></i>
                          Basic Information
                        </h5>
                      </div>

                      <div className="col-md-6">
                        <label htmlFor="company_name" className="form-label">
                          Company Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="company_name"
                          name="company_name"
                          value={companyData.company_name}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      <div className="col-md-6">
                        <label htmlFor="company_code" className="form-label">
                          Company Code
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="company_code"
                          name="company_code"
                          value={companyData.company_code}
                          onChange={handleInputChange}
                          readOnly
                          disabled
                        />
                      </div>

                      <div className="col-12">
                        <label htmlFor="address" className="form-label">
                          Address
                        </label>
                        <textarea
                          className="form-control"
                          id="address"
                          name="address"
                          rows="3"
                          value={companyData.address}
                          onChange={handleInputChange}
                        />
                      </div>

                      <div className="col-md-4">
                        <label htmlFor="city" className="form-label">
                          City
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="city"
                          name="city"
                          value={companyData.city}
                          onChange={handleInputChange}
                        />
                      </div>

                      <div className="col-md-4">
                        <label htmlFor="state" className="form-label">
                          State
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="state"
                          name="state"
                          value={companyData.state}
                          onChange={handleInputChange}
                        />
                      </div>

                      <div className="col-md-4">
                        <label htmlFor="country" className="form-label">
                          Country
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="country"
                          name="country"
                          value={companyData.country}
                          onChange={handleInputChange}
                        />
                      </div>

                      {/* Contact Information */}
                      <div className="col-12 mt-4">
                        <h5 className="border-bottom pb-2 mb-3">
                          <i className="bi bi-telephone me-2"></i>
                          Contact Information
                        </h5>
                      </div>

                      <div className="col-md-6">
                        <label htmlFor="phone" className="form-label">
                          Phone
                        </label>
                        <input
                          type="tel"
                          className="form-control"
                          id="phone"
                          name="phone"
                          value={companyData.phone}
                          onChange={handleInputChange}
                        />
                      </div>

                      <div className="col-md-6">
                        <label htmlFor="email" className="form-label">
                          Email
                        </label>
                        <input
                          type="email"
                          className="form-control"
                          id="email"
                          name="email"
                          value={companyData.email}
                          onChange={handleInputChange}
                        />
                      </div>

                      {/* Registration Numbers */}
                      <div className="col-12 mt-4">
                        <h5 className="border-bottom pb-2 mb-3">
                          <i className="bi bi-file-text me-2"></i>
                          Registration Numbers
                        </h5>
                      </div>

                      <div className="col-md-6">
                        <label htmlFor="gst_number" className="form-label">
                          GST Number
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="gst_number"
                          name="gst_number"
                          value={companyData.gst_number}
                          onChange={handleInputChange}
                        />
                      </div>

                      <div className="col-md-6">
                        <label htmlFor="it_tan" className="form-label">
                          IT TAN
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="it_tan"
                          name="it_tan"
                          value={companyData.it_tan}
                          onChange={handleInputChange}
                        />
                      </div>

                      <div className="col-md-6">
                        <label htmlFor="pf_registration" className="form-label">
                          PF Registration
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="pf_registration"
                          name="pf_registration"
                          value={companyData.pf_registration}
                          onChange={handleInputChange}
                        />
                      </div>

                      <div className="col-md-6">
                        <label htmlFor="esi_registration" className="form-label">
                          ESI Registration
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="esi_registration"
                          name="esi_registration"
                          value={companyData.esi_registration}
                          onChange={handleInputChange}
                        />
                      </div>

                      {/* PF Settings */}
                      <div className="col-12 mt-4">
                        <h5 className="border-bottom pb-2 mb-3">
                          <i className="bi bi-percent me-2"></i>
                          PF Settings
                        </h5>
                      </div>

                      <div className="col-md-4">
                        <label htmlFor="pf_percentage" className="form-label">
                          Employee PF Percentage (%)
                        </label>
                        <div className="input-group">
                          <input
                            type="number"
                            className="form-control"
                            id="pf_percentage"
                            name="pf_percentage"
                            value={companyData.pf_percentage}
                            onChange={handleInputChange}
                            min="0"
                            max="100"
                            step="0.01"
                          />
                          <span className="input-group-text">%</span>
                        </div>
                      </div>

                      <div className="col-md-4">
                        <label htmlFor="pf_employer_percentage" className="form-label">
                          Employer PF Percentage (%)
                        </label>
                        <div className="input-group">
                          <input
                            type="number"
                            className="form-control"
                            id="pf_employer_percentage"
                            name="pf_employer_percentage"
                            value={companyData.pf_employer_percentage}
                            onChange={handleInputChange}
                            min="0"
                            max="100"
                            step="0.01"
                          />
                          <span className="input-group-text">%</span>
                        </div>
                      </div>

                      <div className="col-md-4">
                        <label htmlFor="pf_ceiling" className="form-label">
                          PF Ceiling Amount (₹)
                        </label>
                        <div className="input-group">
                          <span className="input-group-text">₹</span>
                          <input
                            type="number"
                            className="form-control"
                            id="pf_ceiling"
                            name="pf_ceiling"
                            value={companyData.pf_ceiling}
                            onChange={handleInputChange}
                            min="0"
                            step="0.01"
                          />
                        </div>
                      </div>

                      {/* ESI Settings */}
                      <div className="col-12 mt-4">
                        <h5 className="border-bottom pb-2 mb-3">
                          <i className="bi bi-shield me-2"></i>
                          ESI Settings
                        </h5>
                      </div>

                      <div className="col-md-4">
                        <label htmlFor="esi_percentage" className="form-label">
                          Employee ESI Percentage (%)
                        </label>
                        <div className="input-group">
                          <input
                            type="number"
                            className="form-control"
                            id="esi_percentage"
                            name="esi_percentage"
                            value={companyData.esi_percentage}
                            onChange={handleInputChange}
                            min="0"
                            max="100"
                            step="0.01"
                          />
                          <span className="input-group-text">%</span>
                        </div>
                      </div>

                      <div className="col-md-4">
                        <label htmlFor="esi_employer_percentage" className="form-label">
                          Employer ESI Percentage (%)
                        </label>
                        <div className="input-group">
                          <input
                            type="number"
                            className="form-control"
                            id="esi_employer_percentage"
                            name="esi_employer_percentage"
                            value={companyData.esi_employer_percentage}
                            onChange={handleInputChange}
                            min="0"
                            max="100"
                            step="0.01"
                          />
                          <span className="input-group-text">%</span>
                        </div>
                      </div>

                      <div className="col-md-4">
                        <label htmlFor="esi_ceiling" className="form-label">
                          ESI Ceiling Amount (₹)
                        </label>
                        <div className="input-group">
                          <span className="input-group-text">₹</span>
                          <input
                            type="number"
                            className="form-control"
                            id="esi_ceiling"
                            name="esi_ceiling"
                            value={companyData.esi_ceiling}
                            onChange={handleInputChange}
                            min="0"
                            step="0.01"
                          />
                        </div>
                      </div>

                      {/* Status */}
                      <div className="col-md-6 mt-4">
                        <label htmlFor="status" className="form-label">
                          Status
                        </label>
                        <select
                          className="form-select"
                          id="status"
                          name="status"
                          value={companyData.status}
                          onChange={handleInputChange}
                        >
                          <option value="Active">Active</option>
                          <option value="Inactive">Inactive</option>
                        </select>
                      </div>

                      {/* Submit Button */}
                      <div className="col-12 mt-4">
                        <div className="d-flex justify-content-end gap-2">
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={goToDashboard}
                          >
                            <i className="bi bi-x-circle me-2"></i>
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={saving}
                          >
                            {saving ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Saving...
                              </>
                            ) : (
                              <>
                                <i className="bi bi-check-circle me-2"></i>
                                Save Changes
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Navigation Footer */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card border-0">
            <div className="card-body text-center py-3">
              <div className="btn-group" role="group">
                <button
                  className="btn btn-outline-primary"
                  onClick={goToHome}
                >
                  <i className="bi bi-house me-2"></i>
                  Back to Home
                </button>
                <button
                  className="btn btn-outline-success"
                  onClick={goToDashboard}
                >
                  <i className="bi bi-speedometer2 me-2"></i>
                  Back to Dashboard
                </button>
                <button
                  className="btn btn-outline-warning"
                  onClick={goToAdminPanel}
                >
                  <i className="bi bi-gear me-2"></i>
                  Admin Panel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanySettings;